<template>
  <div class="calendar-container">
    <div
      class="collapse-btn-wrapper"
      :style="{ left: isLeftOpen ? 'calc(334px - 14px)' : 'calc(40px - 16px)' }"
    >
      <button
        data-testid="my-calendar.index.collapse-left-panel-btn"
        class="collapse-expand-button"
        @click="toggleLeft"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" :style="{ transform: isLeftOpen ? 'none' :  'rotate(180deg)'}">
          <path d="M17.59 6L19 7.41L14.42 12L19 16.59L17.59 18L11.59 12L17.59 6Z" fill="#374957"/>
          <path d="M11 6L12.41 7.41L7.83 12L12.41 16.59L11 18L5 12L11 6Z" fill="#374957"/>
        </svg>
      </button>
    </div>
    <div :class="['left', { collapsed: !isLeftOpen }]" class="transition-all duration-500 ease-in-out ">
      <LeftCalendar 
        :move-date="leftMoveDate"
        @move-to-date="setRightMoveDate"
      />
      <MeetingRequest/>
    </div>
    <div :class="['right', { shifted: !isLeftOpen }]">
      <RightCalendarHeader 
        @switch-tab="switchTab"
        @create-new-event="createNewEvent"
      />
      <RightCalendar 
        v-if="activeTab === 'calendar'"
        :refresh="refresh"
        :move-date="rightMoveDate"
        @move-to-date="setLeftMoveDate"
        @open-schedule-preview="handleEventClick('preview', $event)"
        @open-schedule-detail="handleEventClick('detail', $event)"
      />
      <RightTask v-else/>
    </div>

    <SchedulePreview
      :key="schedulePreviewKey"
      :visible="showPreview"
      :active-tab="scheduleActiveTab"
      @expand="expandPreview"
      @close="closePreview"
      @update:active-tab="scheduleActiveTab = $event"
    />
    <ScheduleDetail
      :key="scheduleDetailKey"
      
      :visible="showDetail"
      :active-tab="scheduleActiveTab"
      @close="closeDetail"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted,watch } from 'vue'
import LeftCalendar from './sections/LeftCalendar.vue'
import MeetingRequest from './sections/MeetingRequest.vue'
import RightCalendarHeader from './sections/RightCalendarHeader.vue'
import RightCalendar from './sections/RightCalendar.vue'
import SchedulePreview from './sections/schedule/SchedulePreview.vue'
import ScheduleDetail from './sections/schedule/ScheduleDetail.vue'
import RightTask from './sections/task/RightTask.vue'
import moment from 'moment'
import { useStore } from 'vuex'

const store = useStore()

const refresh = ref(false)
const isLeftOpen = ref(true)
const toggleLeft = () => {
  isLeftOpen.value = !isLeftOpen.value
  refresh.value = !refresh.value
}

const activeTab = ref('calendar');
// const activeTab = ref('task');
const switchTab = (tab) => {
  activeTab.value = tab;
};

const showPreview = ref(false)          
const showDetail = ref(false)           

const scheduleActiveTab = ref('appointment')

const schedulePreviewKey = computed(() => {
  const eventInfo = store.state.calendar.currentEventInfo || {}
  return [
    eventInfo.event_id,
    eventInfo.event_title,
    eventInfo.title,
    eventInfo.preview_start,
    eventInfo.start_time,
    showPreview.value,
  ].join('-')
})

const scheduleDetailKey = computed(() => {
  const eventInfo = store.state.calendar.currentEventInfo || {}
  const taskInfo = store.state.calendar.currentTaskInfo || {}
  return [
    eventInfo.event_id,
    eventInfo.event_title,
    eventInfo.title,
    taskInfo.task_id,
    taskInfo.title,
    showDetail.value,
  ].join('-')
})


const handleEventClick = (mode, info) => {
  let eventInfo = { 
    ...info,
  }
  // console.log(eventInfo,'eventInfo')
  store.dispatch('calendar/setPreScheduleInfo', {})
  store.dispatch('calendar/setCurrentTaskInfo', null)
  store.dispatch('calendar/setCurrentEventInfo', eventInfo)
  scheduleActiveTab.value = 'appointment'
  if (mode === 'preview') {
    showPreview.value = true
    showDetail.value = false

  } else if (mode === 'detail') {
    showPreview.value = false
    showDetail.value = true
  }
}

const createNewEvent = () => {
  showPreview.value = false
  showDetail.value = true
  scheduleActiveTab.value = 'appointment'
  store.dispatch('calendar/setCurrentTaskInfo', null)
  store.dispatch('calendar/setPreScheduleInfo', {})
  store.dispatch('calendar/setCurrentEventInfo', {})
}

const leftMoveDate = ref(null)
const rightMoveDate = ref(null)
const setLeftMoveDate = (date) => {
  leftMoveDate.value = date
}
const setRightMoveDate = (date) => {
  rightMoveDate.value = date
}


const expandPreview = (info) => {
  // from meeting request card

  if (info) {
    // console.log(info,'setCurrentEventInfo')
    store.dispatch('calendar/setCurrentEventInfo', info)
  }
  showPreview.value = false
  showDetail.value = true
}



const closePreview = () => {
  showPreview.value = false
  store.dispatch('calendar/setCurrentEventInfo', null)
  store.dispatch('calendar/setCurrentTaskInfo', null)
}
const closeDetail = async () => {
  showDetail.value = false
  store.dispatch('calendar/setCurrentEventInfo', null)
  store.dispatch('calendar/setCurrentTaskInfo', null)
  let rawEventList = store.state.calendar.rawEventList
  store.commit('calendar/SET_RAW_EVENT_LIST', rawEventList)
}


const initializeCalendar = async (userInfo) => {
  if (!userInfo) {
    console.warn('UserInfo not available');
    return;
  }
  
  const clinic_id = userInfo.clinic_id;
  const customerId = userInfo.customer_id;
  
  console.log('Initializing calendar with clinic_id:', clinic_id, 'customerId:', customerId);

  if (!store.state.calendar.rawEventTypeList?.length) {
    await store.dispatch('calendar/fetchPracticeEventTypes');
  }
  
  store.dispatch('calendar/fetchClinic', { clinicId: clinic_id });

  store.dispatch('calendar/fetchMeetingRequests'); // Fetch meeting requests on mount

  const getOrCreateCalendarInput = {
    calendar_owner_id: customerId,
    role: userInfo.user_roles?.includes('clinic') ? 'clinicadmin' : 'provider',
    practice_id: clinic_id 
  }
  store.dispatch('calendar/getOrCreateCalendar', getOrCreateCalendarInput).then(() => {
    store.dispatch('calendar/fetchCanceledMeetingRequests', { 
      calendar_ids: [store.state.calendar.creatorCalendarId],
      start_date: moment().subtract(3, 'months').format('YYYY-MM-DD'),
      end_date: moment().add(2, 'months').format('YYYY-MM-DD'),
    }); // Fetch canceled meeting requests last 1 year
  });
};

onMounted(() => {
  const userInfo = store.state.login?.userInfo;
  
  if (userInfo) {
    // UserInfo already available, initialize immediately
    initializeCalendar(userInfo);
  } else {
    // Wait for userInfo to be populated
    console.log('Waiting for userInfo to be loaded...');
    const unwatch = watch(
      () => store.state.login?.userInfo,
      (newUserInfo) => {
        if (newUserInfo) {
          console.log('UserInfo loaded:', newUserInfo);
          initializeCalendar(newUserInfo);
          unwatch(); // Stop watching once initialized
        }
      },
      { immediate: true }
    );
  }
  
  // Check Zoom authentication status
  store.dispatch('calendar/checkZoomAuth');
  
  document.addEventListener('mousedown', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

const handleClickOutside = (event) => {
  if (showPreview.value || showDetail.value) {
    if (!event.target.closest('.preview-layout') && !event.target.closest('.schedule-detail') && !event.target.closest('.cancel-appointment-dialog') && !event.target.closest('.general-dialog-overlay') && !event.target.closest('.new-library-popper') && !event.target.closest('.custom-popper') && !event.target.closest('.single-selector-dropdown')) {
      closePreview();
      closeDetail();
    }
  }
};

</script>

<style scoped>
.calendar-container {
  display: flex;
  height: calc(100vh - var(--nav-height));
  background: #F9F9F9;
  position: relative;
  overflow: auto;
}
.collapse-btn-wrapper {
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 28px;
  z-index: 10;
  display: flex;
  align-items: center;
  pointer-events: none;
  transition: all 0.5s ease-in-out;
}
.collapse-expand-button {
  pointer-events: auto;
  display: flex;
  padding: 24px 2px;
  align-items: center;
  gap: 12px;
  border-radius: 4px;
  background: #F2F2F2;
  border: none;
  cursor: pointer;
}
.left {
  background: #F9FBFD;
  display: flex;
  flex-direction: column;
  padding: 36px 16px;
  /* gap: 24px; */
  border-radius: 0 10px 10px 0;
  box-shadow: 0px 2px 15px 0px #4A4A4A40;
  margin-right: 14px;
  /* absolute */
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 334px;
  transition: all 0.5s ease-in-out;
  height: calc(100vh - var(--nav-height));
  overflow-y: auto; 
  overflow-x: hidden;
  scrollbar-color: transparent transparent;
}
.left.collapsed {
  transform: translateX(-310px);
}
.left:hover {
  /* overflow-y: auto;  */
  scrollbar-color: #c1c1c1 #f1f1f1; 
}
.right {
  margin-left: 350px;
  transition: all 0.5s ease-in-out;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  min-width: 0;
  height: calc(100vh - var(--nav-height));
  overflow-y: hidden;
}
.right.shifted {
  margin-left: 64px;
}
</style>
